import { env } from "@/lib/env";

/**
 * Runtime configuration for the admin SPA.
 *
 * Goal: build the bundle ONCE and promote the same artifact through
 * dev → staging → prod. Anything that differs per environment therefore cannot
 * be baked in at `vite build` time via `import.meta.env`; instead the deploy
 * writes a small `/config.json` next to `index.html` and the app fetches it
 * before rendering anything.
 *
 * Two deliberate exclusions from this shape:
 *  - the E2E auth/mock bypass flags (`env.e2eAuthRole`, `env.e2eMockApi`).
 *    Those are a TEST-ONLY backdoor gated on the Vite build mode in `env.ts`
 *    and must never be togglable by a file served from a bucket.
 *  - anything secret. `config.json` is a public, unauthenticated asset.
 */
export type RuntimeConfig = {
  apiBaseUrl: string;
  wsBaseUrl: string;
  cognitoRegion: string;
  cognitoUserPoolId: string;
  cognitoAppClientId: string;
  cognitoDomain: string;
  appName: string;
  appStoreFieldUrl: string;
  appStoreCommandUrl: string;
  mixpanelToken: string;
  mixpanelApiHost: string;
  mixpanelDebug: boolean;
};

/** Keys `config.json` may carry. Anything else in the file is ignored. */
export const RUNTIME_CONFIG_KEYS: ReadonlyArray<keyof RuntimeConfig> = [
  "apiBaseUrl",
  "wsBaseUrl",
  "cognitoRegion",
  "cognitoUserPoolId",
  "cognitoAppClientId",
  "cognitoDomain",
  "appName",
  "appStoreFieldUrl",
  "appStoreCommandUrl",
  "mixpanelToken",
  "mixpanelApiHost",
  "mixpanelDebug",
];

export const RUNTIME_CONFIG_PATH = "/config.json";

/**
 * Build-time values, used when no `config.json` is served. That is the normal
 * case for `vite dev` and the Playwright harness, both of which read
 * `.env.local` / `VITE_*` and have no deploy step to write the file.
 */
export function bakedRuntimeConfig(): RuntimeConfig {
  return {
    apiBaseUrl: env.apiBaseUrl,
    wsBaseUrl: env.wsBaseUrl,
    cognitoRegion: env.cognitoRegion,
    cognitoUserPoolId: env.cognitoUserPoolId,
    cognitoAppClientId: env.cognitoAppClientId,
    cognitoDomain: env.cognitoDomain,
    appName: env.appName,
    appStoreFieldUrl: env.appStoreFieldUrl,
    appStoreCommandUrl: env.appStoreCommandUrl,
    mixpanelToken: env.mixpanelToken,
    mixpanelApiHost: env.mixpanelApiHost,
    mixpanelDebug: env.mixpanelDebug,
  };
}

export type RuntimeConfigSource = "config.json" | "baked-env";

export type LoadRuntimeConfigOptions = {
  /** Injected for tests; defaults to the global `fetch`. */
  fetch?: typeof fetch;
  /** Injected for tests; defaults to `bakedRuntimeConfig()`. */
  fallback?: RuntimeConfig;
  /** Injected for tests; defaults to `RUNTIME_CONFIG_PATH`. */
  path?: string;
  /** Injected for tests; defaults to `console.warn`. */
  warn?: (message: string) => void;
};

let loaded: RuntimeConfig | null = null;
let source: RuntimeConfigSource | null = null;

/**
 * Whether a response can be trusted as `config.json`.
 *
 * CloudFront serves the SPA with a 403/404 → `/index.html` rewrite so deep
 * links work. The trap: a MISSING `config.json` therefore comes back as
 * `200 OK` with the HTML shell in the body. Checking `res.ok` alone would then
 * try to JSON-parse `<!doctype html>` and blow up (or, worse, a future
 * permissive parser could accept junk). The content-type is the tell — the
 * bucket object is uploaded as `application/json` and the fallback is
 * `text/html`. Vite dev has the same shape (history-API fallback), so this
 * guard is also what makes local dev fall through to baked env cleanly.
 */
export function isJsonResponse(res: {
  ok: boolean;
  headers: { get(name: string): string | null };
}): boolean {
  if (!res.ok) return false;
  const type = res.headers.get("content-type") ?? "";
  return /^\s*application\/(?:[\w.+-]+\+)?json\b/i.test(type);
}

/**
 * Overlay the recognised keys of a parsed `config.json` onto the fallback.
 * Unknown keys are dropped; keys of the wrong type are ignored so a typo in
 * the deploy cannot turn a string URL into `undefined` at runtime. Exported for
 * tests.
 */
export function mergeRuntimeConfig(
  fallback: RuntimeConfig,
  raw: unknown,
): RuntimeConfig {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    return { ...fallback };
  }
  const input = raw as Record<string, unknown>;
  const merged: RuntimeConfig = { ...fallback };
  for (const key of RUNTIME_CONFIG_KEYS) {
    const value = input[key];
    if (key === "mixpanelDebug") {
      if (typeof value === "boolean") merged.mixpanelDebug = value;
      else if (value === "true" || value === "false") {
        merged.mixpanelDebug = value === "true";
      }
      continue;
    }
    if (typeof value === "string") merged[key] = value;
  }
  return merged;
}

/**
 * Fetch `/config.json` and make it available via `getConfig()`.
 *
 * Never rejects: any failure (network, non-JSON body, malformed JSON) falls
 * back to the build-time values so the app still boots. Resolves with the
 * config it settled on. Idempotent — a second call returns the already-loaded
 * config without refetching.
 */
export async function loadRuntimeConfig(
  options: LoadRuntimeConfigOptions = {},
): Promise<RuntimeConfig> {
  if (loaded) return loaded;

  const fallback = options.fallback ?? bakedRuntimeConfig();
  const path = options.path ?? RUNTIME_CONFIG_PATH;
  const warn = options.warn ?? ((m: string) => console.warn(m));
  const doFetch = options.fetch ?? globalThis.fetch;

  if (typeof doFetch !== "function") {
    return settle(fallback, "baked-env");
  }

  let res: Response;
  try {
    // no-store: the whole point of the file is that a deploy can change it
    // without a rebuild, so a stale cached copy would defeat the mechanism.
    res = await doFetch(path, { cache: "no-store" });
  } catch (err) {
    warn(`runtime-config: ${path} unreachable (${describe(err)}); using build-time env`);
    return settle(fallback, "baked-env");
  }

  if (!isJsonResponse(res)) {
    // Expected in local dev / e2e (SPA fallback serves index.html). Silent.
    return settle(fallback, "baked-env");
  }

  let raw: unknown;
  try {
    raw = await res.json();
  } catch (err) {
    warn(`runtime-config: ${path} is not valid JSON (${describe(err)}); using build-time env`);
    return settle(fallback, "baked-env");
  }

  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    warn(`runtime-config: ${path} must be a JSON object; using build-time env`);
    return settle(fallback, "baked-env");
  }

  return settle(mergeRuntimeConfig(fallback, raw), "config.json");
}

/** The loaded config. Throws if called before `loadRuntimeConfig()` settled. */
export function getConfig(): RuntimeConfig {
  if (!loaded) {
    throw new Error(
      "runtime-config: getConfig() called before loadRuntimeConfig() resolved. " +
        "Bootstrap in main.tsx must await the loader before rendering or " +
        "configuring Amplify/analytics.",
    );
  }
  return loaded;
}

/** Where the active config came from, or null before load. Useful for diagnostics. */
export function getConfigSource(): RuntimeConfigSource | null {
  return source;
}

/** True once `loadRuntimeConfig()` has settled. */
export function isConfigLoaded(): boolean {
  return loaded !== null;
}

/** Test-only: forget the loaded config so the next load refetches. */
export function resetRuntimeConfigForTests(): void {
  loaded = null;
  source = null;
}

function settle(config: RuntimeConfig, from: RuntimeConfigSource): RuntimeConfig {
  loaded = config;
  source = from;
  return config;
}

function describe(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
